import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Download, WifiOff, RefreshCw, Loader2, Send, Trash2, CheckCircle2, Smartphone, CalendarClock } from "lucide-react";
import { DAY_LABELS } from "../../components/team/WorkScheduleEditor";
import { useAuth } from "../../contexts/AuthContext";
import { useOfflineStatus } from "../../lib/offline/offlineApi";
import { prepareSalarieOffline } from "../../lib/offline/salarieOffline";
import { metaGet, metaSet, kvAll } from "../../lib/offline/db";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";

const b64ToU8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = window.atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from(raw, (c) => c.charCodeAt(0)); };
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function SalarieSettingsPage() {
  const { axiosAuth, user, logout } = useAuth();
  const api = axiosAuth();
  const status = useOfflineStatus();
  const [perm, setPerm] = useState(supported() ? Notification.permission : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState(false);
  const [prep, setPrep] = useState(null);
  const [prepInfo, setPrepInfo] = useState(null);
  const [cacheCount, setCacheCount] = useState(0);
  const [schedule, setSchedule] = useState(null);

  const refreshCache = useCallback(() => { kvAll().then((l) => setCacheCount(l.filter((k) => k.scope === "salarie").length)).catch(() => {}); metaGet("salariePrepared").then(setPrepInfo).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/push/preferences").then((r) => { setPrefs(r.data.categories); setLabels(r.data.labels || {}); }).catch(() => {});
    api.get("/tracking/employees/me/schedule").then((r) => setSchedule(r.data)).catch(() => {});
    if (supported()) navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((s) => setSubscribed(!!s)).catch(() => {});
    refreshCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enablePush = async () => {
    if (!supported()) return toast.error("Notifications non supportées par ce navigateur (sur iPhone : installez d'abord l'app via Partager → Sur l'écran d'accueil)");
    setBusy(true);
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p !== "granted") { toast.warning("Notifications refusées"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data: vapid } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(vapid.public_key) });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, user_agent: navigator.userAgent });
      setSubscribed(true);
      toast.success("Notifications activées sur cet appareil");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'activer les notifications"); } finally { setBusy(false); }
  };

  const disablePush = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
      setSubscribed(false);
      toast.success("Notifications désactivées sur cet appareil");
    } catch { toast.error("Erreur lors de la désactivation"); } finally { setBusy(false); }
  };

  const testPush = async () => {
    try {
      const r = await api.post("/push/test-send", { url: "/salarie" });
      const res = r.data.result || {};
      if (res.sent > 0) toast.success("Notification de test envoyée");
      else toast.warning(res.reason === "no_subscription" ? "Aucun appareil abonné" : `Non envoyée (${res.reason || "échec"})`);
    } catch { toast.error("Échec de l'envoi de test"); }
  };

  const togglePref = async (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    try { await api.put("/push/preferences", { categories: next }); } catch { toast.error("Préférence non enregistrée"); }
  };

  const prepare = async () => {
    setPrep({ done: 0, total: 1 });
    try {
      const info = await prepareSalarieOffline(api, setPrep);
      await metaSet("salariePrepared", info);
      refreshCache();
      toast.success(`Données prêtes hors ligne (${info.urls} éléments)`);
    } catch { toast.error("Préparation interrompue"); } finally { setPrep(null); }
  };

  const clearCache = async () => {
    if (!window.confirm("Vider les données hors ligne de cet appareil ?")) return;
    navigator.serviceWorker?.controller?.postMessage({ type: "CLEAR_CACHES" });
    indexedDB.deleteDatabase("blitzz-offline");
    toast.success("Cache vidé — rechargement…");
    setTimeout(() => window.location.reload(), 800);
  };

  const pushOn = perm === "granted" && subscribed;
  return (
    <div className="p-4 sm:p-8" data-testid="salarie-settings">
      <div className="max-w-2xl mx-auto space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#f7c935] font-black">Blitzz Salarié</div>
          <h1 className="text-2xl font-black">Réglages</h1>
          <div className="text-xs text-slate-400 mt-1">{user?.name} · {user?.email}</div>
        </div>

        <section className="rounded-2xl border border-white/10 bg-[#14101c] p-4 space-y-3" data-testid="salarie-push-section">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Bell className="w-4 h-4 text-[#f7c935]" /><h2 className="text-base font-bold">Notifications push</h2></div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${pushOn ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-slate-300"}`} data-testid="salarie-push-state">{perm === "unsupported" ? "Non supporté" : pushOn ? "Activées" : perm === "denied" ? "Bloquées par le navigateur" : "Désactivées"}</span>
          </div>
          <p className="text-xs text-slate-400">Recevez les messages, tâches et alertes même quand l'app est fermée.</p>
          <div className="flex gap-2 flex-wrap">
            {!pushOn ? (
              <Button onClick={enablePush} disabled={busy || perm === "denied"} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="salarie-push-enable">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Bell className="w-3.5 h-3.5 mr-1" />} Activer sur cet appareil</Button>
            ) : (
              <>
                <Button onClick={testPush} variant="outline" className="border-white/15 text-white text-xs" data-testid="salarie-push-test"><Send className="w-3.5 h-3.5 mr-1" /> Envoyer un test</Button>
                <Button onClick={disablePush} disabled={busy} variant="ghost" className="text-slate-300 text-xs" data-testid="salarie-push-disable"><BellOff className="w-3.5 h-3.5 mr-1" /> Désactiver</Button>
              </>
            )}
          </div>
          {prefs && (
            <div className="divide-y divide-white/5 pt-1" data-testid="salarie-push-prefs">
              {Object.entries(labels).map(([k, label]) => (
                <div key={k} className="flex items-center justify-between py-2.5">
                  <span className="text-sm">{label}</span>
                  <Switch checked={!!prefs[k]} onCheckedChange={(v) => togglePref(k, v)} data-testid={`salarie-pref-${k}`} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#14101c] p-4 space-y-2" data-testid="salarie-schedule-section">
          <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#f7c935]" /><h2 className="text-base font-bold">Mes horaires & rappels de pointage</h2></div>
          {schedule?.schedule ? (
            <>
              <div className="flex flex-wrap gap-1" data-testid="salarie-schedule-days">
                {DAY_LABELS.map((l, i) => <span key={l} className={`px-2 py-0.5 rounded text-[11px] font-bold ${schedule.schedule.days.includes(i) ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/5 text-slate-500"}`}>{l}</span>)}
              </div>
              <div className="text-sm" data-testid="salarie-schedule-hours">{schedule.schedule.start} → {schedule.schedule.end}</div>
              <div className="text-[11px] text-slate-400" data-testid="salarie-schedule-reminders">{schedule.schedule.reminders_enabled ? `Rappel push ${schedule.reminder_delay_min} min après l'heure si l'entrée ou la sortie n'est pas pointée.` : "Rappels désactivés."} Horaires définis par votre responsable.</div>
            </>
          ) : (
            <p className="text-xs text-slate-400" data-testid="salarie-schedule-empty">Aucun horaire défini par votre responsable : pas de rappel automatique de pointage.</p>
          )}
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#14101c] p-4 space-y-3" data-testid="salarie-offline-section">
          <div className="flex items-center gap-2"><WifiOff className="w-4 h-4 text-[#f7c935]" /><h2 className="text-base font-bold">Mode hors ligne</h2></div>
          <p className="text-xs text-slate-400">Vos notifications, messages, tâches, bulletins et documents restent lisibles sans réseau. Les messages envoyés, tâches modifiées et notifications lues sont synchronisés automatiquement au retour du réseau.</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="En cache" value={cacheCount} testid="salarie-cache-count" />
            <Stat label="En attente" value={status.pending} testid="salarie-queue-count" warn={status.pending > 0} />
            <Stat label="Dernière synchro" value={status.lastSync ? new Date(status.lastSync).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—"} testid="salarie-last-sync" />
          </div>
          {prepInfo && <div className="text-[11px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Préparé le {new Date(prepInfo.at).toLocaleString("fr-FR")} · {prepInfo.conversations} conversation(s)</div>}
          {prep && <div className="text-[11px] text-slate-300 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> {prep.done}/{prep.total} {prep.url ? `· ${prep.url}` : ""}</div>}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={prepare} disabled={!!prep || !status.online} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="salarie-prepare-offline"><Download className="w-3.5 h-3.5 mr-1" /> Préparer hors ligne</Button>
            <Button onClick={status.sync} disabled={!status.online || status.pending === 0 || status.syncing} variant="outline" className="border-white/15 text-white text-xs" data-testid="salarie-sync-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${status.syncing ? "animate-spin" : ""}`} /> Synchroniser</Button>
            <Button onClick={clearCache} variant="ghost" className="text-slate-400 text-xs" data-testid="salarie-clear-cache"><Trash2 className="w-3.5 h-3.5 mr-1" /> Vider le cache</Button>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#14101c] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="salarie-install-section">
          <div className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#f7c935]" /><div><div className="text-sm font-bold">Installer sur un autre appareil</div><div className="text-[11px] text-slate-400">Windows, Android, macOS, iPhone/iPad</div></div></div>
          <Link to="/salarie/app" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs text-white" data-testid="salarie-install-page-link">Installateurs</Link>
        </section>

        <div className="flex justify-between items-center pt-2">
          <Link to="/dashboard" className="text-xs text-slate-400 hover:text-white" data-testid="salarie-full-platform-link">Ouvrir la plateforme complète →</Link>
          <button onClick={logout} className="text-xs text-red-300 hover:text-red-200" data-testid="salarie-logout">Se déconnecter</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, warn }) {
  return (
    <div className="rounded-xl bg-[#0b0811] border border-white/5 p-2.5">
      <div className={`text-lg font-black ${warn ? "text-amber-300" : "text-white"}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
    </div>
  );
}
